import React, { type ReactNode, useRef, useState } from 'react';

import ActionButton, {
	type ActionButtonVariant,
} from 'components/ActionButton';
import ScreenReaderAnnouncementText from 'components/ScreenReaderAnnouncementText';
import { useValueChangeEffect } from 'hooks';
import type { HTMLTagName } from 'types';
import { cn } from 'utils/classNames';
import {
	getFocusedElement,
	goToElement,
	selectFirstFocusable,
} from 'utils/dom';
import { afterNextPaint, is } from 'utils/helpers';
import { useI18n } from 'utils/i18n';

interface Props {
	afterListContent?: ReactNode;
	buttonAlignment?: 'left' | 'center';
	buttonClassName?: string;
	buttonText?: string;
	buttonVariant?: ActionButtonVariant;
	children?: ReactNode;
	className?: string;
	hasLoadMoreButton?: boolean;
	isLoading?: boolean;
	itemCountScreenReaderText?: string;
	listClassName?: string;
	listTag?: HTMLTagName;
	/**
	 * What to focus when loading more is done, the first new item's container or
	 * the first interactive/tabbable element in that container, if one exists.
	 */
	loadedItemFocusTarget: 'container' | 'first-interactive';
	onLoadMoreClick: () => unknown;
	onLoadMoreDone?: () => unknown;
}

/**
 * A list with a 'load more' button that handles tabbing order.
 *
 * IMPORTANT! Children should only be the primary items of the list that has
 * the 'load more' behavior, any additional stuff like information messages
 * should be placed outside. Otherwise the tab order calculations may be
 * incorrect.
 */
export default function LoadMoreList({
	afterListContent,
	buttonAlignment = 'left',
	buttonClassName,
	buttonText,
	buttonVariant = 'branded',
	children,
	className,
	hasLoadMoreButton = true,
	isLoading = false,
	itemCountScreenReaderText,
	listClassName,
	listTag: ListTag = 'div',
	loadedItemFocusTarget,
	onLoadMoreClick,
	onLoadMoreDone,
}: Props) {
	const { t } = useI18n();
	const rootRef = useRef<HTMLDivElement>(null);
	const listRef = useRef<HTMLElement>(null);
	const loadMoreButtonRef = useRef<HTMLButtonElement>(null);
	const [itemIndex, setItemIndex] = useState<number | undefined>(undefined);
	const [didLoadFromButton, setDidLoadFromButton] = useState(false);

	// Save the current number of items before more are added.
	const handleLoadMoreClick = () => {
		setItemIndex(listRef.current?.childElementCount);
		setDidLoadFromButton(true);
		onLoadMoreClick();
	};

	// After loading additional items is done, focus the first available element
	// among the newly added items if possible.
	useValueChangeEffect(isLoading, () => {
		if (isLoading || !didLoadFromButton) {
			return;
		}
		setDidLoadFromButton(false);
		// Sadly needed, the DOM isn't always updated by the time this runs which
		// leads to an undefined first new item.
		afterNextPaint(() => {
			onLoadMoreDone?.();
			const focusedElement = getFocusedElement();
			if (
				itemIndex &&
				listRef.current &&
				// Only move focus if the user is still on the load button or if there
				// is no focused element (e.g. focus was on the load button which has
				// now been removed). If load more is pressed and the user tabs away
				// before it's done loading, that choice should be respected.
				(focusedElement === loadMoreButtonRef.current || !focusedElement)
			) {
				const firstNewItem = listRef.current.children[itemIndex];
				if (is.instance(firstNewItem, HTMLElement)) {
					const innerTarget = selectFirstFocusable(firstNewItem);
					if (loadedItemFocusTarget === 'first-interactive' && innerTarget) {
						innerTarget.focus();
					} else {
						goToElement(firstNewItem);
					}
				}
			}
		});
	});

	return (
		<div ref={rootRef} className={className}>
			{itemCountScreenReaderText && (
				<ScreenReaderAnnouncementText
					hidden
					as="span"
					text={itemCountScreenReaderText}
				/>
			)}
			<ListTag ref={listRef} className={listClassName}>
				{children}
			</ListTag>

			{afterListContent}

			{hasLoadMoreButton && (
				<ActionButton
					ref={loadMoreButtonRef}
					onClick={handleLoadMoreClick}
					variant={buttonVariant}
					className={cn(
						buttonClassName,
						buttonAlignment === 'center' && 'mx-auto flex',
					)}
					customState={isLoading ? 'loading' : 'idle'}
					showSuccess={false}
					minimunLoadingTime={250}
				>
					{buttonText || t('generic_show_more_button_text')}
				</ActionButton>
			)}
		</div>
	);
}
LoadMoreList.displayName = 'LoadMoreList';
